import React, { useState, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { Skeleton, useMatchBreakpoints } from 'vvs-uikit'
import { useFetchMyMoles } from 'views/VvsMinerMoleMyMoles/hooks/infoApi'
import { NftDetails } from 'views/VvsMinerMoleMyMoles/types'
import { sortMoles, sortNfts } from 'views/VvsMinerMoleMyMoles/utils/sortMoles'
import { MAX_NFT_SELECTABLE } from 'config/constants/farmBoost'
import { NFT_TYPE } from 'config/constants/nftTypes'

import { Nft } from './Nft'
import { EmptyNFTs } from './EmptyNFTs'

const Wrapper = styled.div<{ showScrollbar?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  grid-column-gap: 15px;
  grid-row-gap: 15px;
  width: 100%;
  max-height: 350px;
  padding: 0 24px;

  ${(props) =>
    props.showScrollbar &&
    css`
      overflow-y: auto;

      ::-webkit-scrollbar {
        display: block;
      }
    `}

  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: repeat(3, 1fr);
    max-height: 500px;
  }
`

const EmptyMolesWrapper = styled.div`
  grid-column: span 3;
  padding-top: 30px;
`

interface NftSelectorViewProps {
  nftIds?: string[]
  loading?: boolean
  noNft?: boolean
  selectedNftIds?: string[]
  onChange?: (newSelectedNftIds: string[], newSelectedNfts: NftDetails[]) => void
  selectable?: boolean
  maxSelect?: number
  nftBorderColor?: string
  type?: NFT_TYPE
  dataList?: NftDetails[]
}

const EMPTY_DATA_LIST = []

/**
 * when selectedNftIds prop is supplied, this is a controlled comp
 * when nftIds is null, undefined or empty array, this component will render loading
 * supplied noNft prop as true to render no NFT prompting to marketplace
 */
const NftSelectorView: React.FC<NftSelectorViewProps> = ({
  nftIds,
  loading,
  noNft,
  selectable = true,
  onChange,
  selectedNftIds,
  maxSelect = MAX_NFT_SELECTABLE,
  nftBorderColor,
  type = NFT_TYPE.MINER_MOLE,
  dataList = EMPTY_DATA_LIST,
}) => {
  const [nftsList, setNftsList] = useState<(NftDetails & { selected: boolean })[]>([])
  const [myMoles, isMolesFetching] = useFetchMyMoles(nftIds, {
    enabled: type === NFT_TYPE.MINER_MOLE,
  })

  const selectedCount = nftsList.filter((item) => item.selected).length
  const { isMobile } = useMatchBreakpoints()
  const showScrollbar = isMobile ? nftsList.length > 2 : nftsList.length > 6

  useEffect(() => {
    if (type === NFT_TYPE.MINER_MOLE) {
      if (myMoles) {
        setNftsList(
          sortMoles(myMoles).map((mole) => ({
            ...mole,
            selected: selectedNftIds && selectedNftIds.includes(mole.id),
          })),
        )
      } else {
        setNftsList([])
      }
    }

    if (type === NFT_TYPE.ARGO) {
      if (dataList) {
        setNftsList(
          sortNfts(dataList).map((nft) => ({
            ...nft,
            selected: selectedNftIds && selectedNftIds.includes(nft.id),
          })),
        )
      } else {
        setNftsList([])
      }
    }
  }, [dataList, myMoles, selectedNftIds, type])

  const nftsListMapping = nftsList?.map((item) => {
    return (
      <Nft
        key={item.id}
        imgUrl={item.imgUrl}
        name={item.name}
        nftBorderColor={nftBorderColor}
        rarity={item.rarity}
        selectable={selectable}
        selected={item.selected}
        disabled={!item.selected && selectedCount >= maxSelect}
        hideRarity={type !== NFT_TYPE.MINER_MOLE}
        onClick={() => {
          if (selectedCount >= maxSelect && !item.selected) {
            return
          }
          const updatedNftList = nftsList.map((nft) => {
            if (nft.id === item.id) {
              return { ...nft, selected: !nft.selected }
            }
            return nft
          })
          if (!selectedNftIds) {
            setNftsList(updatedNftList)
          }
          const newSelectedNfts = updatedNftList.filter((nft) => nft.selected)
          onChange(
            newSelectedNfts.map((i) => i.id),
            newSelectedNfts,
          )
        }}
      />
    )
  })

  const renderContent = useMemo(() => {
    let content

    switch (type) {
      case NFT_TYPE.MINER_MOLE:
        content =
          !loading && !isMolesFetching && nftIds?.length > 0 ? (
            nftsListMapping
          ) : (
            <>
              <Skeleton height="200px" />
              <Skeleton height="200px" />
              {!isMobile && <Skeleton height="200px" />}
            </>
          )
        break

      case NFT_TYPE.ARGO:
        content =
          dataList?.length > 0 ? (
            nftsListMapping
          ) : (
            <>
              <Skeleton height="200px" />
              <Skeleton height="200px" />
              {!isMobile && <Skeleton height="200px" />}
            </>
          )
        break
      default:
        break
    }

    return content
  }, [dataList?.length, isMobile, isMolesFetching, loading, nftIds?.length, nftsListMapping, type])

  return (
    <Wrapper showScrollbar={showScrollbar}>
      {noNft ? (
        <EmptyMolesWrapper>
          <EmptyNFTs />
        </EmptyMolesWrapper>
      ) : (
        renderContent
      )}
    </Wrapper>
  )
}

export default NftSelectorView
